import { UNASSIGNED_REGION_FILTER_ID } from "@/hera-api/regions-api";

/**
 * Pixel shift from a region's south-east corner. Negative x is left and
 * negative y is up, so the label sits just inside the polygon.
 */
export const REGION_LABEL_ORIGIN = { x: -8, y: -8 };

export type DashboardMapOverlayMode =
  | "none"
  | "jurisdiction-boundaries"
  | "selected-regions";

export type DashboardMapOverlayPlan = {
  mode: DashboardMapOverlayMode;
  /** Jurisdictions passed to the bulk region list. Empty skips that request. */
  regionJurisdictionIds: string[];
  /** Jurisdictions whose census boundaries are drawn. Empty skips those requests. */
  boundaryJurisdictionIds: string[];
  /**
   * Selected region ids drawn with a red dotted border and an outside mask.
   * Empty means those borders are not drawn.
   */
  regionIdsToDraw: string[];
  /**
   * After the bulk list returns, keep hit targets only in the jurisdictions
   * that own the selected regions. Used when an operator has a region selected
   * and no jurisdiction filter.
   */
  narrowHitsToSelectedJurisdictions: boolean;
  /** Region hover/click and outside-click clear. False when nothing is in scope. */
  clicksEnabled: boolean;
};

type LatLng = { lat: number; lng: number };

export type DrawableRegion = {
  id: string;
  /** Same text the Regions page shows for the region. */
  name?: string;
  enabled?: boolean;
  deleted_at?: string | null;
  jurisdiction_id?: string;
  geometry?: { type?: string; coordinates?: unknown } | null;
};

function uniqueIds(ids: readonly string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const id of ids) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    result.push(id);
  }
  return result;
}

/** Region ids that have polygons. "Unassigned" is a filter value, not a shape. */
export function selectableRegionIds(
  selectedRegionIds: readonly string[],
): string[] {
  return uniqueIds(
    selectedRegionIds.filter((id) => id !== UNASSIGNED_REGION_FILTER_ID),
  );
}

/**
 * Jurisdictions chosen in the filter, narrowed to the portal list when that
 * list is present. An empty filter does not expand to every jurisdiction an
 * operator can see. Government maps pass their jurisdiction scope here.
 */
export function applicableRegionJurisdictionIds(
  selectedJurisdictionIds: readonly string[],
  portalJurisdictionIds: readonly string[],
): string[] {
  const selected = uniqueIds(selectedJurisdictionIds);
  if (selected.length === 0) return [];
  const portal = uniqueIds(portalJurisdictionIds);
  if (portal.length === 0) return selected;
  const allowed = new Set(selected);
  return portal.filter((id) => allowed.has(id));
}

/**
 * No real region selected, with jurisdictions in scope: census outlines and
 * an outside mask, plus hit targets for every active region in that scope.
 * Real regions selected: red dotted borders and an outside mask for those
 * regions only, no census outline. Hit targets still cover every active
 * region in scope. An operator with neither a jurisdiction nor a region
 * draws nothing. A selected region without a jurisdiction filter is loaded
 * from the portal list and then narrowed to that region's jurisdictions.
 */
export function resolveDashboardMapOverlay(input: {
  selectedRegionIds: readonly string[];
  selectedJurisdictionIds: readonly string[];
  portalJurisdictionIds: readonly string[];
}): DashboardMapOverlayPlan {
  const jurisdictionScope = applicableRegionJurisdictionIds(
    input.selectedJurisdictionIds,
    input.portalJurisdictionIds,
  );
  const selected = selectableRegionIds(input.selectedRegionIds);
  const portal = uniqueIds(input.portalJurisdictionIds);
  const queryJurisdictionIds =
    jurisdictionScope.length > 0
      ? jurisdictionScope
      : selected.length > 0
        ? portal
        : [];

  if (queryJurisdictionIds.length === 0) {
    return {
      mode: "none",
      regionJurisdictionIds: [],
      boundaryJurisdictionIds: [],
      regionIdsToDraw: [],
      narrowHitsToSelectedJurisdictions: false,
      clicksEnabled: false,
    };
  }

  if (selected.length > 0) {
    return {
      mode: "selected-regions",
      regionJurisdictionIds: queryJurisdictionIds,
      boundaryJurisdictionIds: [],
      regionIdsToDraw: selected,
      narrowHitsToSelectedJurisdictions: jurisdictionScope.length === 0,
      clicksEnabled: true,
    };
  }

  return {
    mode: "jurisdiction-boundaries",
    regionJurisdictionIds: queryJurisdictionIds,
    boundaryJurisdictionIds: jurisdictionScope,
    regionIdsToDraw: [],
    narrowHitsToSelectedJurisdictions: false,
    clicksEnabled: true,
  };
}

export function regionIdsChanged(
  current: readonly string[],
  next: readonly string[],
): boolean {
  if (current.length !== next.length) return true;
  const nextIds = new Set(next);
  return current.some((id) => !nextIds.has(id));
}

function isCoordinatePair(value: unknown): value is [number, number] {
  return (
    Array.isArray(value) &&
    value.length >= 2 &&
    typeof value[0] === "number" &&
    typeof value[1] === "number" &&
    Number.isFinite(value[0]) &&
    Number.isFinite(value[1])
  );
}

function ringFrom(raw: unknown): LatLng[] | null {
  if (!Array.isArray(raw)) return null;
  const ring: LatLng[] = [];
  for (const point of raw) {
    if (!isCoordinatePair(point)) return null;
    ring.push({ lat: point[1], lng: point[0] });
  }
  return ring.length >= 3 ? ring : null;
}

/** Polygon and MultiPolygon rings in Maps lat/lng order. Invalid geometry is skipped. */
export function polygonGroupsFromGeometry(
  geometry: { type?: string; coordinates?: unknown } | null | undefined,
): LatLng[][][] {
  try {
    if (!geometry || !Array.isArray(geometry.coordinates)) return [];
    if (geometry.type === "Polygon") {
      const rings: LatLng[][] = [];
      for (const raw of geometry.coordinates) {
        const ring = ringFrom(raw);
        if (!ring) return [];
        rings.push(ring);
      }
      return rings.length > 0 ? [rings] : [];
    }
    if (geometry.type === "MultiPolygon") {
      const groups: LatLng[][][] = [];
      for (const polygon of geometry.coordinates) {
        if (!Array.isArray(polygon)) continue;
        const rings: LatLng[][] = [];
        let valid = true;
        for (const raw of polygon) {
          const ring = ringFrom(raw);
          if (!ring) {
            valid = false;
            break;
          }
          rings.push(ring);
        }
        if (valid && rings.length > 0) groups.push(rings);
      }
      return groups;
    }
    return [];
  } catch {
    return [];
  }
}

/** South-east corner of a region's rings: minimum latitude, maximum longitude. */
export function regionLabelAnchor(groups: LatLng[][][]): LatLng | null {
  let south = Number.POSITIVE_INFINITY;
  let east = Number.NEGATIVE_INFINITY;
  let found = false;
  for (const rings of groups) {
    for (const ring of rings) {
      for (const point of ring) {
        if (!Number.isFinite(point.lat) || !Number.isFinite(point.lng)) {
          continue;
        }
        found = true;
        if (point.lat < south) south = point.lat;
        if (point.lng > east) east = point.lng;
      }
    }
  }
  return found ? { lat: south, lng: east } : null;
}

/**
 * Active regions can be hovered and toggled. The bulk list already omits
 * deleted rows; this also drops a stale `deleted_at` and disabled regions,
 * which the region facet treats as inactive.
 */
export function isActiveMapRegion(region: DrawableRegion): boolean {
  if (region.deleted_at) return false;
  if (region.enabled === false) return false;
  return polygonGroupsFromGeometry(region.geometry).length > 0;
}

export function regionsForHitTargets(
  regions: readonly DrawableRegion[],
  options: {
    selectedRegionIds: readonly string[];
    narrowToSelectedJurisdictions: boolean;
  },
): DrawableRegion[] {
  const active = regions.filter(isActiveMapRegion);
  if (!options.narrowToSelectedJurisdictions) return active;
  const selected = new Set(selectableRegionIds(options.selectedRegionIds));
  const jurisdictions = new Set(
    active.flatMap((region) =>
      selected.has(region.id) && region.jurisdiction_id
        ? [region.jurisdiction_id]
        : [],
    ),
  );
  if (jurisdictions.size === 0) {
    return active.filter((region) => selected.has(region.id));
  }
  return active.filter(
    (region) =>
      region.jurisdiction_id != null &&
      jurisdictions.has(region.jurisdiction_id),
  );
}

export function regionsToDraw(
  regions: readonly DrawableRegion[],
  regionIdsToDraw: readonly string[],
): DrawableRegion[] {
  const allow = new Set(regionIdsToDraw);
  return regions.filter(
    (region) => allow.has(region.id) && isActiveMapRegion(region),
  );
}

/** Absolute shoelace area of each polygon's outer ring. */
export function polygonOuterArea(groups: LatLng[][][]): number {
  let area = 0;
  for (const rings of groups) {
    const outer = rings[0];
    if (!outer || outer.length < 3) continue;
    let sum = 0;
    for (let index = 0; index < outer.length; index++) {
      const start = outer[index];
      const end = outer[(index + 1) % outer.length];
      sum += (end.lng - start.lng) * (end.lat + start.lat);
    }
    area += Math.abs(sum);
  }
  return area;
}

/**
 * Paint order for overlapping hit areas. Earlier entries sit underneath.
 * The last entry is topmost and receives hover and click: smallest outer-ring
 * area, then the lexicographically greater id when areas tie.
 */
export function orderRegionHitTargets<T extends { id: string; area: number }>(
  regions: readonly T[],
): T[] {
  return [...regions].sort((left, right) => {
    if (left.area !== right.area) return right.area - left.area;
    if (left.id < right.id) return -1;
    if (left.id > right.id) return 1;
    return 0;
  });
}

/**
 * Add a region that is absent and remove it when it is already selected.
 * Other selected ids, including Unassigned, stay in place. Values are the
 * region ids the region facet stores.
 */
export function toggleRegionFilter(
  selectedRegionIds: readonly string[],
  regionId: string,
): string[] {
  if (!regionId || regionId === UNASSIGNED_REGION_FILTER_ID) {
    return [...selectedRegionIds];
  }
  if (selectedRegionIds.includes(regionId)) {
    return selectedRegionIds.filter((id) => id !== regionId);
  }
  return [...selectedRegionIds, regionId];
}

export type MapClickEvent = {
  stop?: () => void;
  domEvent?: {
    clientX?: number;
    clientY?: number;
    target?: EventTarget | null;
  };
};

/**
 * Region clicks call `stop` before toggling the filter so the map background
 * click does not immediately clear the selection. A same-turn map click is
 * ignored as well, in case the Maps event still arrives. An outside map click
 * clears the whole region filter.
 */
export function createRegionClickController(options: {
  clicksEnabled: boolean;
  selectedRegionIds: readonly string[];
  onRegionFilterChange: (regionIds: string[]) => void;
}) {
  let backgroundSuppressed = false;
  const suppressBackgroundClick = () => {
    backgroundSuppressed = true;
    queueMicrotask(() => {
      backgroundSuppressed = false;
    });
  };
  return {
    suppressBackgroundClick,
    onRegionClick(event: MapClickEvent, regionId: string) {
      suppressBackgroundClick();
      event.stop?.();
      if (!options.clicksEnabled || !regionId) return;
      options.onRegionFilterChange(
        toggleRegionFilter(options.selectedRegionIds, regionId),
      );
    },
    onMapClick() {
      if (backgroundSuppressed || !options.clicksEnabled) return;
      options.onRegionFilterChange([]);
    },
  };
}
